import { useState, useCallback, useEffect } from 'react'

import { Divider, Loader } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import QuestionRow from 'src/components/Inputs/QuestionRow/QuestionRow'
import {
  GET_PUBLIC_VOE_RESPONSE_DATA,
  SUBMIT_VOE_RESPONSE_V2,
} from 'src/graphql/voeResponseAuth.graphql'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import {
  toVoeEmployerResponseSubmitInput,
  VOE_RESPONSE_VALIDATION_SUMMARY,
  voeEmployerResponseFormSchema,
  type VoeEmployerResponseFormValues,
} from 'src/lib/voeResponseFormSchema'
import Header from 'src/pages/DriverApplicationPage/components/Header'

import AccidentsCard from '../../ApplicantDetailsPage/tabs/ApplicationTab/components/AccidentsCard'
import ApplicationCompletedScreen from '../../DriverApplicationPage/screens/ApplicationCompletedScreen'
import CompanyInfoCard from '../../VoERequestDetailsPage/components/CompanyInfoCard'
import DriverInfoCard from '../../VoERequestDetailsPage/components/DriverInfoCard'
import RequestContentColumn from '../../VoERequestDetailsPage/components/RequestContentColumn'
import ResponseContentColumn from '../../VoERequestDetailsPage/components/ResponseContentColumn'

interface VoEResponseFormV2Props {
  voeId: string
  sessionToken: string
  onSessionExpired: () => void
}

const VoEResponseFormV2 = ({
  voeId,
  sessionToken,
  onSessionExpired,
}: VoEResponseFormV2Props) => {
  const [showFeedback, setShowFeedback] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<VoeEmployerResponseFormValues>({
    validate: zodResolver(voeEmployerResponseFormSchema),
  })

  const [submitVoEResponse, { loading: loadingSubmit }] = useMutation(
    SUBMIT_VOE_RESPONSE_V2,
    {
      onCompleted: () => {
        setShowFeedback(true)
      },
      onError: () => {
        setSubmitError(
          'Something went wrong while submitting. Please try again or contact support.'
        )
      },
    }
  )

  const { data, loading } = useQuery(GET_PUBLIC_VOE_RESPONSE_DATA, {
    variables: { voeId, sessionToken },
    onCompleted: (result) => {
      handleInitialValues(result?.publicVoEResponseData)
    },
    onError: () => {
      onSessionExpired()
    },
  })

  const voeData = data?.publicVoEResponseData

  const handleSubmit = useCallback(
    (values: VoeEmployerResponseFormValues) => {
      submitVoEResponse({
        variables: {
          voeId,
          sessionToken,
          input: toVoeEmployerResponseSubmitInput(values),
        },
      })
    },
    [submitVoEResponse, voeId, sessionToken]
  )

  const stripTypename = (accidents?: Array<Record<string, unknown>> | null) =>
    (accidents ?? []).map(({ __typename, ...rest }) => rest)

  const handleInitialValues = (voe: any) => {
    if (!voe) return
    const content =
      (voe.status === 'PENDING_REVIEW' || voe.status === 'COMPLETED') &&
      voe.responseContent
        ? voe.responseContent
        : voe.requestContent
    form.setValues(
      (voe.status === 'PENDING_REVIEW' || voe.status === 'COMPLETED') &&
        voe.responseContent
        ? {
            positionHeld: content.positionHeld,
            startDate: new Date(content.startDate),
            endDate: content.endDate ? new Date(content.endDate) : null,
            reasonForLeaving: content.reasonForLeaving,
            trailerTypes: content.trailerTypes,
            wasEmployed: true,
            eligibleForRehire: voe.responseContent.eligibleForRehire,
            isDriverTerminated: content.isDriverTerminated,
            isDriverSubjectToFMCSRs: content.isDriverSubjectToFMCSRs,
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              content.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
            accidents: stripTypename(voe.accidents),
          }
        : {
            wasEmployed: null,
            accidents: stripTypename(voe.accidents),
            positionHeld: voe.requestContent.positionHeld,
            startDate: new Date(voe.requestContent.startDate),
            endDate: voe.requestContent.endDate
              ? new Date(voe.requestContent.endDate)
              : null,
            reasonForLeaving: voe.requestContent.reasonForLeaving,
            trailerTypes: voe.requestContent.trailerTypes,
            eligibleForRehire: null,
            isDriverTerminated: voe.requestContent.isDriverTerminated,
            isDriverSubjectToFMCSRs: voe.requestContent.isDriverSubjectToFMCSRs,
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              voe.requestContent
                .isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
          }
    )
  }

  useEffect(() => {
    if (voeData && Object.keys(form.values).length === 0) {
      handleInitialValues(voeData)
    }
  }, [voeData])

  const handleAccidentsUpdate = useCallback(
    async ({
      accidents,
    }: {
      accidents: VoeEmployerResponseFormValues['accidents']
    }) => {
      form.setFieldValue(`accidents`, accidents)
      return { errors: null }
    },
    []
  )

  const isCompleted = voeData?.status === 'PENDING_REVIEW'

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Loader />
      </div>
    )
  }
  if (!voeData) {
    return null
  }

  return (
    <>
      {!showFeedback && (
        <form
          className="flex h-full flex-col gap-6  overflow-auto px-6 pb-10"
          onSubmit={form.onSubmit(
            (values) => {
              setSubmitError(null)
              handleSubmit(values)
            },
            () => {
              setSubmitError(VOE_RESPONSE_VALIDATION_SUMMARY)
            }
          )}
        >
          <Header companyId={voeData.companyId} />
          <CompanyInfoCard voe={voeData} />
          <DriverInfoCard voe={voeData} noApplicantLink={true} hideSsn={true} />

          <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
            <div className="flex flex-col gap-6 p-6">
              <div className="text-md font-semibold text-doubleNickel-gray-900">
                Validate information
              </div>
              <div className="text-sm text-doubleNickel-gray-500">
                Use the buttons next to the fields or reject the information. In
                case of invalid information, please insert the correct
                information.
              </div>
              <div className="flex flex-row items-center gap-4 rounded-lg bg-doubleNickel-brand-25 px-4 py-3">
                <IconAlertCircle className="fill-none stroke-doubleNickel-brand-500" />
                <div className="flex-1">
                  <QuestionRow
                    required
                    text="Was the applicant employed by your company?"
                    value={form.values.wasEmployed}
                    error={form.errors.wasEmployed as string | undefined}
                    handleChange={(value: string) =>
                      form.setFieldValue(`wasEmployed`, value === 'true')
                    }
                    editing={!isCompleted}
                  />
                </div>
              </div>
              {form.values.wasEmployed === false && (
                <div className="text-sm text-doubleNickel-gray-600">
                  Thanks — nothing else is needed. Submit and we will stop
                  contacting you about this driver.
                </div>
              )}
              <div
                className={`flex flex-row gap-8 ${
                  form.values.wasEmployed === false ? 'hidden' : ''
                }`}
              >
                <RequestContentColumn
                  form={form}
                  request={voeData.requestContent}
                />
                <Divider orientation="vertical" />
                <ResponseContentColumn
                  editing={!isCompleted}
                  form={form}
                  request={voeData.requestContent}
                />
              </div>
              <div
                className={`flex flex-row items-center gap-4 rounded-lg bg-doubleNickel-brand-25 px-4 py-3 ${
                  form.values.wasEmployed === false ? 'hidden' : ''
                }`}
              >
                <IconAlertCircle className="fill-none stroke-doubleNickel-brand-500" />
                <div className="flex-1">
                  <QuestionRow
                    required
                    text="Is this applicant eligible for rehire?"
                    value={form.values.eligibleForRehire}
                    error={form.errors.eligibleForRehire as string | undefined}
                    handleChange={(value: string) =>
                      form.setFieldValue(`eligibleForRehire`, value === 'true')
                    }
                    editing={!isCompleted}
                  />
                </div>
              </div>
            </div>
          </div>
          {form.values.wasEmployed !== false && form.values.accidents && (
            <AccidentsCard
              values={form.values.accidents}
              handleUpdateApplication={handleAccidentsUpdate}
              readOnly={isCompleted}
            />
          )}
          {!isCompleted && (
            <div className="flex w-full flex-col items-end gap-2">
              {submitError && (
                <div className="text-sm text-red-600">{submitError}</div>
              )}
              <div className="flex flex-row justify-end">
                {!loadingSubmit && <Button text="Submit" type="submit" />}
                {loadingSubmit && <Loader size="sm" />}
              </div>
            </div>
          )}
        </form>
      )}
      {showFeedback && (
        <div className="flex min-h-screen items-center justify-center">
          <ApplicationCompletedScreen
            title="Information Submitted!"
            subTitle={'Contact support with any questions'}
          />
        </div>
      )}
    </>
  )
}

export default VoEResponseFormV2
